import { I18n } from "@lingui/core";
import { Trans } from "@lingui/macro";
import { Heading, Text } from "@otrium/atoms";
import Link from "next/link";
import { LoginForm } from "src/molecules/LoginForm";
import styles from "../LoginOrRegisterView.module.scss";
import { SocialButtons } from "./SocialButtons";

type LoginProps = {
  isCheckoutPath?: boolean;
  i18n: I18n;
  registerUrl: string;
  handleRegisterClick: () => void;
  handleContinueAsGuest?: () => void;
};

function Login({
  isCheckoutPath,
  i18n,
  registerUrl,
  handleRegisterClick,
  handleContinueAsGuest,
}: LoginProps): JSX.Element {
  return (
    <>
      <section className={styles.header}>
        <Heading
          data-testid="login-page-heading"
          as="h1"
          color={"tone.black"}
          fontSize={["32px", "32px", "32px", "48px"]}
          fontFamily="PlusJakartaSans"
          lineHeight={["40px", "40px", "40px", "58px"]}
          sx={{
            textTransform: "none",
          }}
        >
          <Trans>Log in</Trans>
        </Heading>
        <Text
          fontSize={["16px"]}
          lineHeight={["24px"]}
          data-testid="login-page-welcome-message"
        >
          <Trans>300+ iconic brands you know and love.</Trans>
        </Text>
      </section>

      <SocialButtons isCheckoutPath={isCheckoutPath} i18n={i18n} />

      <div className={styles.divider}>
        <Text as="span" color="tone.black">
          <Trans>or</Trans>
        </Text>
      </div>

      <LoginForm
        data-testid="login-page-login-form"
        isCheckoutPath={isCheckoutPath}
        onContinueAsGuest={handleContinueAsGuest}
      />

      <Link
        className={styles.forgotPasswordLink}
        data-testid="login-page-forgot-password-link"
        href="/reset-password"
        prefetch={false}
      >
        <Trans>Forgot password</Trans>
      </Link>

      <section className={styles.mobileTabSwitch}>
        <Text
          as="span"
          display="flex"
          placeItems="center"
          justifyContent="center"
          gap={8}
          color="tone.black"
          fontSize={1}
          lineHeight="1.5"
        >
          <Trans>New to Otrium?</Trans>
          <Link
            href={registerUrl}
            data-testid="login-page-register-link"
            onClick={handleRegisterClick}
          >
            <Text
              fontWeight="600"
              color="tone.black"
              sx={{
                textDecoration: "underline",
                ":hover": { cursor: "pointer" },
              }}
            >
              <Trans>Register</Trans>
            </Text>
          </Link>
        </Text>
      </section>
    </>
  );
}

export { Login };
